import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { path } from 'ramda'
import { isSomething, calculateEvapotranspiration } from '../../common/ambient'
import FormattedDataPoint from './FormattedDataPoint'

export default function EvapoWidget({ currentDevice }) {
  const [etValue, setEtValue] = useState(null)
  const [error, setError] = useState(null)

  const { lastData } = currentDevice
  const coords = path(['info', 'coords', 'coords'], currentDevice)

  const solarradday = lastData ? (lastData.solarradday || path(['hl', 'solarradiation', 's'], lastData)) : null 

  // Check required fields
  const hasRequiredData =
    lastData &&
    isSomething(path(['hl', 'tempf', 'h'], lastData)) &&
    isSomething(path(['hl', 'tempf', 'l'], lastData)) &&
    isSomething(path(['hl', 'humidity', 'h'], lastData)) &&
    isSomething(path(['hl', 'humidity', 'l'], lastData)) &&
    isSomething(solarradday) &&
    (isSomething(lastData.windspeedmph) || isSomething(path(['hl', 'windspeedmph', 's'], lastData))) &&
    coords && coords.lat && coords.lon

  // Calculate wind speed average (fallback to current value if hl data not available)
  const avgWindSpeed = path(['hl', 'windspeedmph', 's'], lastData) &&
                       path(['hl', 'windspeedmph', 'c'], lastData)
    ? lastData.hl.windspeedmph.s / lastData.hl.windspeedmph.c
    : lastData.windspeedmph

  // Get elevation from device info, fallback to sea level
  const elevation = path(['info', 'coords', 'elevation'], currentDevice) || 0

  useEffect(() => {
    if (!hasRequiredData) {
      setEtValue(null)
      setError(null)
      return
    }

    try {
      const result = calculateEvapotranspiration({
        maxTempF: lastData.hl.tempf.h,
        minTempF: lastData.hl.tempf.l,
        solarRadiationDailyJ: solarradday,
        avgWindSpeedMPH: avgWindSpeed,
        elevationMeters: elevation,
        maxHumidity: lastData.hl.humidity.h,
        minHumidity: lastData.hl.humidity.l,
        latitude: coords.lat,
        date: new Date()
      })

      if (result) {
        setEtValue(result)
        setError(null)
      } else {
        setError('Unable to calculate ET')
      }
    } catch (err) {
      console.error('ET calculation error:', err)
      setError('Calculation error')
    }
  }, [hasRequiredData, elevation, lastData, avgWindSpeed, coords, solarradday])

  return (
    <div className="device-evapo-widget">
      {error && (
        <div className="evapo-error">{error}</div>
      )}

      {!error && !hasRequiredData && (
        <div className="evapo-error">
          ET requires temperature, humidity, wind, and solar radiation data.
        </div>
      )}

      {!error && hasRequiredData && etValue && (
        <div className="evapo-display center-aligned">
          <FormattedDataPoint
            className="et-value"
            type="dailyrainin"
            value={etValue.inches}
            after="/day"
          />
        </div>
      )}
    </div>
  )
}

EvapoWidget.propTypes = {
  currentDevice: PropTypes.object.isRequired
}

EvapoWidget.displayName = 'EvapoWidget'
